import { useState, useEffect, Fragment, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';

import {
  AppBar, Avatar, Box, Button, Container, Divider, IconButton, Menu, MenuItem, Stack, Toolbar, Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Widgets';

import { useAuthenticator } from '@aws-amplify/ui-react';

import RevelataLogoSvg from '../static/RevelataIncLogo.svg';
import PreviewIconSvg from '../static/PreviewIcon.svg';

import { ampli } from '../index.js';
import { createStripePortalSession } from '../Subscription/StripePortalRedirect';
import SketchedLogoSpinner from '../Spinner/Spinner';
import { getSubscriptionStatus } from '../RequireAuth/RequireAuth';
import { brand, green } from '../getTheme/getTheme';
import { useIsMobile } from '../../common/Utils';

import { useSubscriptionStatus } from '../Contexts/Contexts';
import { openPreviewModal } from '../Dashboard/Dashboard';

const ShimmeringMenuItem = styled(MenuItem)(({ theme }) => ({
  '@keyframes shimmer': {
    '0%': {
      color: '#4CAF50',
    },
    '50%': {
      color: '#2196F3',
    },
    '100%': {
      color: '#4CAF50',
    },
  },
  animation: 'shimmer 2s infinite',
  fontWeight: 'bold',
}));

function CustomNavEntry({entryLabel, entry, buttonStyle}) {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef(null);
	const navigate = useNavigate();

	if (typeof entry === 'string') {
		return (
			<Button
				color="inherit"
				onClick={() => navigate(entry)}
				sx={{ minWidth: '100px', ...buttonStyle }}
			>
				{entryLabel}
			</Button>
		);
	} else if (typeof entry === 'object' && entry !== null) {
		const handleMenuOpen = () => {
			setOpen(true);
		};

		const handleMenuClose = () => {
			setOpen(false);
		};

		const handleMenuItemClick = (route) => {
			// Close menu immediately before navigation to prevent rendering glitch
			setOpen(false);
			// Use setTimeout to ensure the menu closes before navigation starts
			setTimeout(() => {
				navigate(route);
			}, 0);
		};

		return (
			<Fragment>
				<Button
					ref={buttonRef}
					color="inherit"
					onClick={handleMenuOpen}
					sx={{ minWidth: '100px', ...buttonStyle }}
				>
					{entryLabel}
				</Button>
				<Menu
					anchorEl={buttonRef.current}
					open={open && buttonRef.current !== null}
					onClose={handleMenuClose}
					disablePortal={false}
					keepMounted={false}
				>
					{Object.entries(entry).map(([label, route]) => (
						<MenuItem
							key={label}
							sx={{ textTransform: 'none', fontSize: { md: "16px" }, p: 2 }}
							onClick={() => handleMenuItemClick(route)}
						>
							{label}
						</MenuItem>
					))}
				</Menu>
			</Fragment>
		);
	} else {
		return null;
	}
}

export function TopAppBar() {
	const { route, user, authStatus } = useAuthenticator((context) => [context.route, context.user, context.authStatus]);
	const navigate = useNavigate();
	const [subscriptionStatus, setSubscriptionStatus] = useSubscriptionStatus();
	
	const [anchorElUser, setAnchorElUser] = useState(null);
	const [anchorElNav, setAnchorElNav] = useState(null);

	// const location = useLocation();
	// const [tabValue, setTabValue] = useState(defaultTabValue);
	// const [isDashboardActive, setIsDashboardActive] = useState(false);

	const [cookies, setCookie, removeCookie] = useCookies()
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);

	const isMobile = useIsMobile();

	/************************** Menu definitions ************************/

	//@@@TODO Swap navEntries out for this
	let mobileNavEntries = {
		"Home": "/",
		"deepKPI": "/deepkpi/app", 
		"deepAnalysis": "/deepanalysis/app", 
		"deepInsight": "/deepinsight",
		"Tutorials": "/tutorials",
		"Blog": "/blog",
		"Pricing": "/pricing",
		"Referral Rewards": "/coupon",
	};

	let loggedOutNavEntries = {		
		"Platform": {
			"deepKPI": "/deepkpi/app", 
			"deepAnalysis": "/deepanalysis/app", 
			"deepInsight": "/deepinsight",
		},
		"Learn": {
			"Video tutorials": "/tutorials",
			"deepKPI FAQ": "/deepkpi/support",
		},
		"Blog": "/blog",
		"Pricing": "/pricing",
	}

	let loggedInNavEntries = {
		"Referral Rewards": "/coupon",
	};


	// Logged in user menu entries
	const loggedInMenuEntries = {
		...(user?.username && !user.username.startsWith('google_') ? {
			'Profile': '/profile',
			'Change Password': '/changepassword',
		} : {}),
		...(subscriptionStatus !== 'no_stripe_account' ? {
			'Manage Subscription': async () => {
				setSubscriptionLoading(true);
				try {
					await createStripePortalSession(user.signInUserSession.idToken.payload.sub);
				} catch (error) {
					console.error('Failed to create Stripe portal session:', error);
				} finally {
					setSubscriptionLoading(false);
				}
			},
		} : {
			'Start Trial': '/subscribe'
		})
	};
		

	// useEffect(() => {
	// 	// Check if we're on the dashboard route
	// 	if (location.pathname === '/dashboard') {
	// 		setIsDashboardActive(true);
	// 		setTabValue(-1); // No tab selected
	// 		return;
	// 	}
		
	// 	// Check if we're on the deepkpi/newsfeed route
	// 	if (location.pathname === '/deepkpi/newsfeed') {
	// 		setIsDashboardActive(true);
	// 		setTabValue(-1); // No tab selected
	// 		return;
	// 	}
		
	// 	// Reset dashboard active state for other routes
	// 	setIsDashboardActive(false);
		
	// 	setTabValue(
	// 		() => {
	// 			const navPaths = Object.values(navEntries);
	// 			const currentPathIndex = navPaths.findIndex(path => {
	// 				// Match first level route by comparing up to the first slash after the initial slash
	// 				const pathFirstLevel = path.split('/', 2).join('/');
	// 				const locationFirstLevel = location.pathname.split('/', 2).join('/');
	// 				return locationFirstLevel === pathFirstLevel;
	// 			});
	// 			// console.log("location.pathname", location.pathname);
	// 			// console.log("navPaths", navPaths);
	// 			// console.log("currentPathIndex", currentPathIndex);
	// 			return currentPathIndex !== -1 ? currentPathIndex : 0;
	// 		}
	// 	);
	// }, [location.pathname]);

    useEffect(() => {
        const checkSubscription = async () => {
            if (authStatus === 'authenticated' && user) {
                const status = await getSubscriptionStatus();
                setSubscriptionStatus(status);
            }
        };
        checkSubscription();
    }, [authStatus, user]);

    useEffect(() => {
        if (subscriptionStatus === 'not_stripe_elligible') {
            navigate('/logout');
        }
    }, [subscriptionStatus]);

	/*************************** Event handlers *************************/

	// Handlers for nav menu on mobile 
	const handleOpenNavMenu = (event) => {
	  	setAnchorElNav(event.currentTarget);
	};
	const handleCloseNavMenu = () => {
		//console.log("handleCloseNavMenu", anchorElNav);
	  	setAnchorElNav(null);
	};
	const handleNavMenuPick = async (event, route) => {
		handleCloseNavMenu();
		if (typeof loggedInMenuEntries[route] === 'function') {
			await loggedInMenuEntries[route]();
		}
	};

	// Handlers for user menu on desktop
	const handleOpenUserMenu = (event) => {
		setAnchorElUser(event.currentTarget);
	};
	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};
	const handleUserMenuPick = (e, menuEntry) => {
		handleCloseUserMenu();
		if (typeof loggedInMenuEntries[menuEntry] === 'function') {
			loggedInMenuEntries[menuEntry]();
		}
	};

	// // Handler for nav tabs on desktop
	// const handleTabChange = (event, newValue) => {
	// 	setTabValue(newValue);
	// };

	const onClickSignInButton = (location) => {
		ampli.commonLoginInitiated();
	}
		
	/*************************** Component  ***************************/
	
	let userMenuElement = null;  // This is for user menu on desktop

	// Show nothing when Auth hasn't settled yet
	if (route === 'idle') {
		return null;
	}

	// Bubble buttons for free trial and sign in
	if (authStatus !== 'authenticated') {
		userMenuElement = (
			<>
				<Button 
					variant='contained' 
					sx={{
						borderRadius: 50, 
						textTransform: 'none', 
						height: '36px', 
						mr: 1,
						whiteSpace: 'nowrap',
						minWidth: 'max-content',
						display: { xs: 'none', md: 'flex', lg: 'flex' }
					}} 
					size="large" 
					href={"/signup"}
					onClick={() => { 
						ampli.commonCreateAccountInitiated(); 
					}}
				>
						Free Trial
				</Button>
				<Button 
					variant='outlined' 
					href={"/login"}
					sx={{ 
						borderColor: "white", 
						textTransform: 'none', 
						borderRadius: 50, 
						color: 'white', 
						display: { xs: 'none', md: 'flex', lg: 'flex' },
						width: '100px',
						height: '36px'
					}} 
					onClick={() => { 
						onClickSignInButton("Top App Bar"); 
					}}
				>
					Sign In
				</Button>
			</>
		)
	} else {
		// Authenticated user menu:
		userMenuElement = (
			// Avatar (or spinner if we're loading)
			<Box sx={{ flexGrow: 0 }}>

				<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
					{subscriptionLoading ? (
						<SketchedLogoSpinner size={32}/>
					) : (
						<>
							<Avatar 
								sx={{ height: "32px", width: "32px" }} 
								alt={user?.signInUserSession?.idToken?.payload?.given_name || user?.signInUserSession?.idToken?.payload?.email || '-'}
							>
								{(user?.signInUserSession?.idToken?.payload?.given_name[0] || user?.signInUserSession?.idToken?.payload?.email[0] || '-')}
							</Avatar>
						</>
					)}
				</IconButton>

				<Menu
					sx={{ mt: '45px' }}
					id="menu-appbar"
					anchorEl={anchorElUser}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					keepMounted
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					open={Boolean(anchorElUser)}
					onClose={handleCloseUserMenu}
				>
					{Object.keys(loggedInMenuEntries).map((displayName) => (
						displayName === 'Referral Rewards'
							? (authStatus === 'authenticated' && subscriptionStatus !== 'no_stripe_account') && (
								<ShimmeringMenuItem
									key={displayName}
									component={RouterLink}
									to={loggedInMenuEntries[displayName]}
									onClick={(e) => { handleUserMenuPick(e, displayName) }}
								>
									<Typography>{displayName}</Typography>
								</ShimmeringMenuItem>
							)
							: (
								<MenuItem
									key={displayName}
									component={RouterLink}
									to={loggedInMenuEntries[displayName]}
									onClick={(e) => { handleUserMenuPick(e, displayName) }}
								>
									<Typography>{displayName}</Typography>
								</MenuItem>
							)
					))}

					<MenuItem 
						key="logout"
						component={RouterLink}
						to={"/logout"}
						onClick={() => { handleCloseUserMenu(); removeCookie("DEEPKPI_betaLoginModal") }}
					>
							<Typography>Sign Out</Typography>
					</MenuItem>
				</Menu>
			</Box>);
	};

	/* Mobile version */
	if (isMobile) {
		return (
			<AppBar position="fixed" sx={{ boxShadow: 0,  backgroundImage: 'none', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
				<Container maxWidth="false" sx={{ padding: 0 }}>
					<Toolbar disableGutters sx={{ padding: "5px", height: "50px" }}>

						{/* Logo */}
						<Box sx={{ display: { xs: 'flex'}}} component={RouterLink} to={mobileNavEntries["Home"]}>
							<img src={RevelataLogoSvg} alt="Revelata Logo" height={33} />
						</Box>
						{authStatus === 'authenticated' && subscriptionStatus && subscriptionStatus !== 'no_stripe_account' && (
							<Box 
								component="img" 
								src={PreviewIconSvg} 
								alt="PREVIEW" 
								sx={{
									height: {md: 16}, 
									ml: 1,
									cursor: 'pointer',
									'&:hover': { opacity: 0.8 }
								}}
								onClick={openPreviewModal}
							/>
						)}
						
						{/* Spacer */}
						<Box sx={{ flexGrow: 1, display: 'flex' }}>
							{/* This is a spacer to right align the nav tabs */}
						</Box>

						{/* Hamburger Menu*/}
						<Box sx={{ flexGrow: 0, display: { xs: 'flex'}}}>
							<IconButton
								size="large"
								aria-label="account of current user"
								aria-controls="menu-appbar"
								aria-haspopup="true"
								onClick={handleOpenNavMenu}
								color="inherit"
							>
								<MenuIcon /> 
							</IconButton>
						
							<Menu
								id="menu-appbar"
								anchorEl={anchorElNav}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								keepMounted
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
								open={Boolean(anchorElNav)}
								onClose={handleCloseNavMenu}
								sx={{
									display: { xs: 'block', md: 'none' },
								}}
							>
								{Object.keys(mobileNavEntries).map((page) => {
									// Hide "Home" tab when user is authenticated
									if (page === "Home" && authStatus === "authenticated") {
										return null;
									}
									
									if (
										page === "Referral Rewards" &&
										!(authStatus === "authenticated" && subscriptionStatus !== "no_stripe_account")
									) {
										return null;
									}
									return (
										<MenuItem
											key={page}
											component={RouterLink}
											to={mobileNavEntries[page]}
											onClick={(e) => {
												handleNavMenuPick(e, page);
											}}
										>
											<Typography>{page}</Typography>
										</MenuItem>
									);
								})}
							<Divider/>
							{
								authStatus !== 'authenticated' 
								? 
								<Box>
									<MenuItem key="Free Trial" 
										component="a" href={process.env.REACT_APP_WWW_URL + "/signup"}
										onClick={(e) => { 
											handleNavMenuPick(e, "/signup"); 										
											ampli.commonCreateAccountInitiated(); 
										}
									}
									>
										<Typography>FREE TRIAL</Typography>
									</MenuItem>

									<MenuItem key="Sign In" 
										component="a" href={process.env.REACT_APP_WWW_URL + "/login"}
										onClick={(e) => { 
											handleNavMenuPick(e, "/login");
											ampli.commonLoginInitiated();
										}
									}
									>
										<Typography>Sign In</Typography>
									</MenuItem>
								</Box>
								:
								<Box>
								{
									Object.keys(loggedInMenuEntries).map((displayName) => (
										<MenuItem 
											key={displayName} 
											component={RouterLink} 
											to={loggedInMenuEntries[displayName]} 
											onClick={(e) => { handleNavMenuPick(e, displayName) }}
										>
											<Typography>{displayName}</Typography>
										</MenuItem>
									))
								}

								<MenuItem 
									key="logout"
									component={RouterLink}
									to={"/logout"}
									onClick={() => { handleCloseNavMenu(); removeCookie("DEEPKPI_betaLoginModal") }}
								>
									<Typography>Sign Out</Typography>
								</MenuItem>
								</Box>
							}						
							</Menu> 
						</Box> 
						{/* <Stack direction="row" justifyContent="end" sx={{display: {xs: 'none', md: 'flex'}}}>
							{userMenuElement}
						</Stack> */}
					</Toolbar>
				</Container>
			</AppBar>
		);
	}		

	/* Desktop version */
	return (
		<AppBar position="fixed" sx={{ boxShadow: 0,  backgroundImage: 'none', zIndex: (theme) => theme.zIndex.drawer + 1, borderBottom: authStatus === 'authenticated' ? 1 : 0, borderColor: 'grey.800' }}>
			<Container maxWidth="false" sx={{ padding: 0 }}>
				<Toolbar disableGutters sx={{ padding: "5px", height: "50px" }}>
					{/* Logo (with preview icon) */}
					<Box sx={{ display: { md: 'flex' } }} component={RouterLink} to={mobileNavEntries["Home"]}>
						<img src={RevelataLogoSvg} alt="Revelata Logo" height={50} />
					</Box>					
					{subscriptionStatus && (subscriptionStatus === 'trialing' || subscriptionStatus === 'active') && (
						<Box 
							component="img" 
							src={PreviewIconSvg} 
							alt="PREVIEW" 
							sx={{
								height: {md: 16}, 
								ml: 1,
								cursor: 'pointer',
								'&:hover': { opacity: 0.8 }
							}}
							onClick={openPreviewModal}
						/>
					)}
					<Box sx={{ flexGrow: 1, display: 'flex' }}>
						{/* This is a spacer to right align the nav tabs */}
					</Box>

					<Box sx={{ display: { md: 'flex' }, gap: 2, mr: 4 }}>
						{
							(authStatus === 'authenticated') ? (
								Object.keys(loggedInNavEntries).map((displayName, idx) => {
									// Only show referral rewards if they're logged in and have a subscription:
									if (displayName === "Referral Rewards" 
										&& subscriptionStatus === 'no_stripe_account') {
										return null;
									}

									// Show the rest of the tabs:
									return (
										<CustomNavEntry
											entryLabel={displayName}
											entry={loggedInNavEntries[displayName]}
											buttonStyle={
													displayName === "Referral Rewards"
													? {
														textTransform: 'none',
														fontSize: { md: "18px" },
														marginRight: { xs: 1, sm: 2, md: 1 },
														animation: 'shimmer 2s infinite',
														'@keyframes shimmer': {
															'0%': { color: green[300] },
															'50%': { color: brand[300] },
															'100%': { color: green[300] },
														},
													}
													: { textTransform: 'none', fontSize: { md: "18px" }, marginRight: { xs: 1, sm: 2, md: 1 } }
											}											
										/>
									);
								})
							) : (
								Object.keys(loggedOutNavEntries).map((displayName, idx) => {
									// Show the rest of the tabs:
									return (
										<CustomNavEntry
											entryLabel={displayName}
											entry={loggedOutNavEntries[displayName]}
											buttonStyle={ { textTransform: 'none', fontSize: { md: "18px" }, minWidth: 'max-content'} }											
										/>
									);
								})
							)
						}
					</Box>

					{/* User menu on desktop */}
					<Stack direction="row" justifyContent="end" sx={{display: {md: 'flex'}}}>
						{userMenuElement}
					</Stack>

				</Toolbar>
			</Container>
		</AppBar>	
	);
}
export default TopAppBar;
